import type { Layout } from '../../components/viewToggleMetrics';
import { SWITCHES, TIME_SLOTS, WEEKDAYS, type SwitchKey, type TimeSlot, type Weekday } from '../../shared/taxonomy';
import { DEFAULT_MODE, TRAVEL_MODES, type TravelMode } from '../travel/walkshed';
import { DISTANCE_STEPS, EMPTY_FILTERS, type FilterState } from './filters';

/**
 * The filters and the layout in the query string (contract §4, §10), so a
 * filtered view can be shared or reloaded:
 *
 *   /explore?q=&day=mon,tue&time=morning&role=steward&topic=water&on=inperson,free,under18
 *    &within=30&from=current&mode=bike&sort=nearest&hidden=1&past=1&view=calendar
 *
 * `mode` is written only when it isn't transit; `from` only for Current
 * location, whose position is never in the URL — the recipient's own device is
 * asked instead.
 *
 * The scope is the path (`/explore`, `/saved`), not a parameter: `savedOnly`
 * is never written, and read only as the old `saved=1` (`isLegacySaved`), which
 * the page turns into `/saved`. Only what differs from the defaults is written.
 * Anything unrecognised is dropped on the way in, so an old or hand-edited link
 * still opens.
 */

const LAYOUTS: readonly Layout[] = ['list', 'calendar', 'map'];

/** `?view=`; absent, or anything it does not know, is the list. */
export function layoutFromSearch(search: string | URLSearchParams): Layout {
  const view = new URLSearchParams(search).get('view');
  return (LAYOUTS as readonly string[]).includes(view ?? '') ? (view as Layout) : 'list';
}

/** A link from before the Saved pill: `/explore?saved=1` is `/saved` now. */
export const isLegacySaved = (search: string | URLSearchParams) => new URLSearchParams(search).get('saved') === '1';

/**
 * A shared calendar (contract §18): `/saved?ids=<saved list>&city=nyc`, the
 * ids as `shared/ics/savedList.ts` writes them. Read here, and carried through
 * `toSearch` untouched, so the filters writing the address back never drop it.
 */
export interface SharedParams {
  ids: string;
  city: string | null;
}

export function sharedFromSearch(search: string | URLSearchParams): SharedParams | null {
  const p = new URLSearchParams(search);
  const ids = p.get('ids')?.trim();
  return ids ? { ids, city: p.get('city')?.trim() || null } : null;
}

/** Switch keys as the URL spells them: lower case. */
const SWITCH_PARAM: Record<SwitchKey, string> = {
  inPerson: 'inperson',
  virtual: 'virtual',
  indoor: 'indoor',
  outdoor: 'outdoor',
  free: 'free',
  accessible: 'accessible',
  noExperience: 'noexperience',
  under18: 'under18',
};
const SWITCH_OF = new Map(Object.entries(SWITCH_PARAM).map(([k, v]) => [v, k as SwitchKey]));

const list = (value: string | null) => (value ? value.split(',').map((v) => v.trim()).filter(Boolean) : []);

/** In a fixed order, so the same filters always make the same URL. */
function ordered<T extends string>(selected: Set<T>, order: readonly T[]): T[] {
  return order.filter((v) => selected.has(v));
}

export function toSearchParams(f: FilterState, layout: Layout = 'list', shared: SharedParams | null = null): URLSearchParams {
  const p = new URLSearchParams();
  if (shared) {
    p.set('ids', shared.ids);
    if (shared.city) p.set('city', shared.city);
  }
  if (f.query.trim()) p.set('q', f.query.trim());
  if (f.weekdays.size) p.set('day', ordered(f.weekdays, WEEKDAYS.map((d) => d.key)).join(','));
  if (f.slots.size) p.set('time', ordered(f.slots, TIME_SLOTS.map((s) => s.id)).join(','));
  if (f.roles.size) p.set('role', [...f.roles].sort().join(','));
  if (f.topics.size) p.set('topic', [...f.topics].sort().join(','));
  if (f.switches.size) {
    p.set('on', ordered(f.switches, SWITCHES.map((s) => s.key)).map((k) => SWITCH_PARAM[k]).join(','));
  }
  if (f.withinMinutes != null) p.set('within', String(f.withinMinutes));
  if (f.origin === 'current') p.set('from', 'current');
  if (f.mode !== DEFAULT_MODE) p.set('mode', f.mode);
  if (f.sort !== 'soonest') p.set('sort', f.sort);
  if (f.showHidden) p.set('hidden', '1');
  if (f.showPast) p.set('past', '1');
  if (layout !== 'list') p.set('view', layout);
  return p;
}

/**
 * `fallbackMode` is the mode stored with the home, used when the link names none.
 */
export function fromSearchParams(p: URLSearchParams, fallbackMode: TravelMode = DEFAULT_MODE): FilterState {
  const days = new Set<string>(WEEKDAYS.map((d) => d.key));
  const slots = new Set<string>(TIME_SLOTS.map((s) => s.id));
  const within = Number(p.get('within'));
  const mode = p.get('mode');
  return {
    ...EMPTY_FILTERS,
    query: p.get('q') ?? '',
    weekdays: new Set(list(p.get('day')).filter((d): d is Weekday => days.has(d))),
    slots: new Set(list(p.get('time')).filter((s): s is TimeSlot => slots.has(s))),
    // Keys are checked against the city's taxonomy where they are drawn; an unknown one matches nothing.
    roles: new Set(list(p.get('role'))),
    topics: new Set(list(p.get('topic'))),
    switches: new Set(list(p.get('on')).map((s) => SWITCH_OF.get(s.toLowerCase())).filter((s): s is SwitchKey => Boolean(s))),
    withinMinutes: (DISTANCE_STEPS as readonly number[]).includes(within) ? within : null,
    origin: p.get('from') === 'current' ? 'current' : 'home',
    mode: TRAVEL_MODES.includes(mode as TravelMode) ? (mode as TravelMode) : fallbackMode,
    sort: p.get('sort') === 'nearest' ? 'nearest' : 'soonest',
    // The path's to say, not the query's.
    savedOnly: false,
    showHidden: p.get('hidden') === '1',
    showPast: p.get('past') === '1',
  };
}

/** The query string, with its "?", or "" when every filter is at its default and the layout is the list. */
export function toSearch(f: FilterState, layout: Layout = 'list', shared: SharedParams | null = null): string {
  // Commas left literal, as the contract writes them: `day=mon,tue`, not `day=mon%2Ctue`.
  const s = toSearchParams(f, layout, shared).toString().replace(/%2C/gi, ',');
  return s ? `?${s}` : '';
}
